import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import Navigation from '../components/Navigation';
import UpgradeModal from '../components/UpgradeModal';
import QuotaUpgradeModal from '../components/QuotaUpgradeModal';
import { useQuotaGate } from '../components/QuotaDisplay';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Building2, TrendingUp, FileText, ExternalLink, Shield, Download, Calendar, ArrowLeft, Heart, MessageSquare, Bookmark, UserPlus, Phone, Video, GitBranch, Clock, CheckCircle2, XCircle, Eye, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NDA_TEMPLATE = `
NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is entered into by and between the parties on the SeedSynch platform.

1. CONFIDENTIAL INFORMATION
The Disclosing Party agrees to share certain confidential and proprietary information including but not limited to:
- Business plans and strategies
- Financial information and projections
- Technical specifications and intellectual property
- Customer and market data
- Any other sensitive business information

2. OBLIGATIONS
The Receiving Party agrees to:
- Keep all Confidential Information strictly confidential
- Not disclose to any third parties without prior written consent
- Use the information solely for the purpose of evaluating investment opportunities
- Return or destroy all materials upon request

3. TERM
This Agreement shall remain in effect for a period of 5 years from the date of acceptance.

4. GOVERNING LAW
This Agreement shall be governed by applicable laws.

By clicking "I Accept", you acknowledge that you have read, understood, and agree to be bound by the terms of this Agreement.
`;

// Helper function to display field value or "Not provided" placeholder
const FieldValue = ({ value, textClass, emptyClass = "text-slate-400 italic" }) => {
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
    return <span className={emptyClass}>Not provided</span>;
  }
  return <span className={textClass}>{value}</span>;
};

export default function ProfileView() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { upgradeModal, checkFeature, closeUpgradeModal } = useFeatureAccess();
  const { modalState, closeModal, consumeAndGate } = useQuotaGate();
  const [profile, setProfile] = useState(null);
  const [profileType, setProfileType] = useState(null);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [showNdaModal, setShowNdaModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [quotaChecked, setQuotaChecked] = useState(false);
  const [isMatched, setIsMatched] = useState(false);
  const [matchId, setMatchId] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showNewTimeModal, setShowNewTimeModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [investmentDecision, setInvestmentDecision] = useState('');
  const [proposedSlots, setProposedSlots] = useState(['', '', '']);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [meetingPlatform, setMeetingPlatform] = useState('zoom');
  const [meetingLink, setMeetingLink] = useState('');
  const [proposalMessage, setProposalMessage] = useState('');
  const [featureAccess, setFeatureAccess] = useState({
    messaging: { allowed: false, requiredPlan: null },
    unlimited_matches: { allowed: false, requiredPlan: null }
  });

  // Preview mode - shows how the profile appears to the other role
  const previewMode = searchParams.get('preview');
  const isPreviewMode = !!previewMode && userId === user?.user_id;

  useEffect(() => {
    // Track profile view quota when viewing someone else's profile
    const trackViewAndFetch = async () => {
      // Skip quota tracking for preview mode (viewing own profile)
      if (isPreviewMode) {
        fetchProfile();
        return;
      }
      
      // Don't track quota for viewing own profile
      if (userId !== user?.user_id && !quotaChecked) {
        const quotaType = user?.role === 'founder' 
          ? 'investor_previews' 
          : user?.role === 'investor' 
            ? 'browsing' 
            : 'match_suggestions';
        
        const allowed = await consumeAndGate(quotaType, user?.role);
        setQuotaChecked(true);
        
        if (!allowed) {
          // User exceeded quota - modal will show, don't fetch profile
          setLoading(false);
          return;
        }
      }
      
      fetchProfile();
      checkMatchStatus();
      checkFeatureAccess();
      if (user?.role === 'investor') {
        checkNdaStatus();
      }
    };
    
    trackViewAndFetch();
  }, [userId, user, isPreviewMode]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/profile/${userId}`);
      setProfile(response.data.profile);
      setProfileType(response.data.profile_type);
    } catch (error) {
      console.error('Error fetching profile:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to load profile';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkFeatureAccess = async () => {
    try {
      // Check messaging feature access
      const msgRes = await axios.get(`${API}/subscription/check-feature?feature=messaging`);
      // Check matching feature access  
      const matchRes = await axios.get(`${API}/subscription/check-feature?feature=unlimited_matches`);
      
      setFeatureAccess({
        messaging: {
          allowed: msgRes.data.allowed,
          requiredPlan: msgRes.data.allowed ? null : (msgRes.data.upgrade_info?.required_plan_name || 'Pro')
        },
        unlimited_matches: {
          allowed: matchRes.data.allowed,
          requiredPlan: matchRes.data.allowed ? null : (matchRes.data.upgrade_info?.required_plan_name || 'Pro')
        }
      });
    } catch (error) {
      console.error('Error checking feature access:', error);
    }
  };

  const checkMatchStatus = async () => {
    try {
      const matchesRes = await axios.get(`${API}/matches`);
      const match = matchesRes.data.find(m => 
        m.user1_id === userId || m.user2_id === userId
      );
      if (match) {
        setIsMatched(true);
        setMatchId(match.match_id);
        // Fetch milestone for this match
        try {
          const milestoneRes = await axios.get(`${API}/milestones/${match.match_id}`);
          setMilestone(milestoneRes.data);
        } catch (err) {
          // Milestone may not exist yet
          console.log('No milestone found for match');
        }
      } else {
        setIsMatched(false);
        setMatchId(null);
        setMilestone(null);
      }
    } catch (error) {
      console.error('Error checking match status:', error);
    }
  };

  const checkNdaStatus = async () => {
    try {
      const response = await axios.get(`${API}/nda/status/${userId}`);
      setNdaAccepted(response.data.accepted);
    } catch (error) {
      console.error('Error checking NDA status:', error);
    }
  };

  const handleLike = async () => {
    setActionLoading(true);
    await checkFeature('unlimited_matches', async () => {
      try {
        const response = await axios.post(`${API}/discovery/like`, { target_user_id: userId });
        setIsLiked(true);
        if (response.data.matched) {
          setIsMatched(true);
          toast.success("It's a match! You can now message each other. 🎉");
        } else {
          toast.success('Profile liked! They will be notified if they like you back.');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to like profile');
      }
    });
    setActionLoading(false);
  };

  const handleMessage = async () => {
    if (!isMatched) {
      toast.info('You need to match with this user first before messaging.');
      return;
    }
    
    await checkFeature('messaging', () => {
      axios.get(`${API}/matches`).then(res => {
        const match = res.data.find(m => 
          m.user1_id === userId || m.user2_id === userId
        );
        if (match) {
          navigate(`/messages/${match.match_id}`);
        } else {
          toast.error('Match not found');
        }
      });
    });
  };

  const handleUpgradeSuccess = async () => {
    // Refresh user context and feature access after upgrade
    if (refreshUser) {
      await refreshUser();
    }
    await checkFeatureAccess();
    closeUpgradeModal();
    toast.success('Plan upgraded! Actions are now unlocked.');
  };

  // Milestone action handlers
  const handleMilestoneAction = async (action, extraData = {}) => {
    if (!matchId) return;
    setActionLoading(true);
    try {
      const payload = {
        match_id: matchId,
        action: action,
        ...extraData
      };
      const response = await axios.post(`${API}/milestones/update`, payload);
      setMilestone(response.data);
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProposePitch = async () => {
    const validSlots = proposedSlots.filter(s => s.trim());
    if (validSlots.length === 0) {
      toast.error('Please select at least one date/time');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = {
        match_id: matchId,
        action: 'propose_pitch_session',
        proposed_slots: validSlots.map(s => new Date(s).toISOString()),
        meeting_platform: meetingPlatform || 'zoom',
        meeting_link: meetingLink || null,
        message: proposalMessage || null
      };
      const response = await axios.post(`${API}/milestones/update`, payload);
      setMilestone(response.data);
      setShowPitchModal(false);
      setProposedSlots(['', '', '']);
      setMeetingPlatform('zoom');
      setMeetingLink('');
      setProposalMessage('');
      toast.success('Pitch proposal sent! They will be notified.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send proposal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPitch = async () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = {
        match_id: matchId,
        action: 'confirm_pitch_session',
        selected_slot: selectedSlot
      };
      const response = await axios.post(`${API}/milestones/update`, payload);
      setMilestone(response.data);
      setShowConfirmModal(false);
      setSelectedSlot('');
      toast.success('Pitch session confirmed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm session');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProposeNewTime = async () => {
    const validSlots = proposedSlots.filter(s => s.trim());
    if (validSlots.length === 0) {
      toast.error('Please select at least one date/time');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = {
        match_id: matchId,
        action: 'propose_new_time',
        proposed_slots: validSlots.map(s => new Date(s).toISOString()),
        meeting_platform: meetingPlatform || 'zoom',
        meeting_link: meetingLink || null,
        message: proposalMessage || null
      };
      const response = await axios.post(`${API}/milestones/update`, payload);
      setMilestone(response.data);
      setShowNewTimeModal(false);
      setProposedSlots(['', '', '']);
      toast.success('New times proposed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to propose new times');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclinePitch = async () => {
    if (!window.confirm('Are you sure you want to decline this pitch proposal?')) return;
    
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/milestones/update`, {
        match_id: matchId,
        action: 'decline_pitch_session'
      });
      setMilestone(response.data);
      toast.success('Proposal declined');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecordDecision = async () => {
    if (!investmentDecision) {
      toast.error('Please select a decision');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = {
        match_id: matchId,
        action: 'record_investment_decision',
        decision_outcome: investmentDecision
      };
      const response = await axios.post(`${API}/milestones/update`, payload);
      setMilestone(response.data);
      setShowDecisionModal(false);
      setInvestmentDecision('');
      toast.success('Investment decision recorded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record decision');
    } finally {
      setActionLoading(false);
    }
  };

  // Render Actions Panel - shows all actions with lock/unlock status
  const renderActionsPanel = () => {
    if (user?.user_id === userId) return null; // Don't show actions on own profile
    
    // Theme classes
    const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const textPrimary = isDark ? 'text-white' : '${textPrimary}';
    const textSecondary = isDark ? 'text-slate-300' : '${textSecondary}';
    const actionBg = isDark ? 'border-slate-600 bg-slate-700/50' : 'border-slate-300 bg-slate-100/50';
    const actionBgDisabled = isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-300/50 bg-slate-100/30';
    const iconBg = isDark ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 ${textSecondary}';
    const iconBgDisabled = isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-700/50 ${textSecondary}';
    
    return (
      <Card className={`shadow-sm p-6 mb-6 ${cardBg}`}>
        <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}>
          <UserPlus size={20} className="text-blue-500" />
          Actions
        </h3>
        
        <div className="space-y-4">
          {/* Like / Express Interest Action */}
          <div className={`p-4 rounded-lg border ${
            featureAccess.unlimited_matches.allowed 
              ? actionBg 
              : actionBgDisabled
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  featureAccess.unlimited_matches.allowed 
                    ? iconBg 
                    : iconBgDisabled
                }`}>
                  <Heart size={20} className={isLiked ? 'fill-current' : ''} />
                </div>
                <div>
                  <p className={`font-medium ${textPrimary}`}>
                    {isLiked ? 'Interest Sent' : 'Express Interest'}
                  </p>
                  <p className={`text-xs ${textSecondary}`}>
                    {featureAccess.unlimited_matches.allowed 
                      ? '✓ Available in your plan' 
                      : `🔒 Requires ${featureAccess.unlimited_matches.requiredPlan}`
                    }
                  </p>
                </div>
              </div>
              <Button
                onClick={handleLike}
                disabled={isLiked || isMatched || actionLoading}
                className={`${
                  featureAccess.unlimited_matches.allowed && !isLiked && !isMatched
                    ? 'btn-primary' 
                    : isLiked 
                      ? 'bg-green-600 hover:bg-green-600 cursor-not-allowed'
                      : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                {isLiked ? 'Sent ✓' : isMatched ? 'Matched' : 'Like'}
              </Button>
            </div>
          </div>

          {/* Match Status & Relationship Actions */}
          {isMatched && (
            <div className="space-y-4">
              {/* Relationship Status Section - Clear indicator of current stage and next action */}
              <div className={`p-5 rounded-lg border ${isDark ? 'border-blue-700/30 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}>
                <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <GitBranch size={20} className="text-blue-500" />
                  Relationship Status
                </h3>
                
                {/* Current Stage Display */}
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className={`text-xs uppercase tracking-wide ${textMuted}`}>Current Stage</p>
                    <p className={`text-xl font-semibold ${textPrimary} mt-1`}>
                      {milestone?.current_state === 'matched' && 'Matched'}
                      {milestone?.current_state === 'initial_conversation' && 'Initial Conversation'}
                      {milestone?.current_state === 'pitch_proposed' && 'Pitch Proposed'}
                      {milestone?.current_state === 'pitch_session_scheduled' && 'Pitch Scheduled'}
                      {milestone?.current_state === 'pitch_session_completed' && 'Pitch Completed'}
                      {milestone?.current_state === 'due_diligence' && 'Due Diligence'}
                      {milestone?.current_state === 'investment_decision' && 'Investment Decision'}
                      {!milestone?.current_state && 'Matched'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs uppercase tracking-wide ${textMuted}`}>Next Milestone</p>
                    <p className={`text-xl font-semibold text-blue-500 mt-1`}>
                      {(!milestone?.current_state || milestone?.current_state === 'matched') && 'Initial Conversation'}
                      {milestone?.current_state === 'initial_conversation' && 'Pitch Session'}
                      {milestone?.current_state === 'pitch_proposed' && 'Confirm Pitch'}
                      {milestone?.current_state === 'pitch_session_scheduled' && 'Pitch Completed'}
                      {milestone?.current_state === 'pitch_session_completed' && 'Due Diligence'}
                      {milestone?.current_state === 'due_diligence' && 'Investment Decision'}
                      {milestone?.current_state === 'investment_decision' && 'Complete!'}
                    </p>
                  </div>
                </div>
                
                {/* Recommended Next Action */}
                <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800/70' : 'bg-white'}`}>
                  <p className={`text-xs uppercase tracking-wide ${textMuted} mb-2`}>Recommended Next Action</p>
                  <div className="flex items-center gap-3">
                    {/* Action buttons based on current state */}
                    {(!milestone?.current_state || milestone?.current_state === 'matched') && (
                      <Button 
                        size="sm" 
                        className="btn-primary flex-grow sm:flex-grow-0"
                        onClick={() => handleMilestoneAction('mark_initial_conversation')}
                        data-testid="next-action-initial-call-btn"
                      >
                        <Phone size={14} className="mr-2" />
                        Request Initial Call
                      </Button>
                    )}
                    {milestone?.current_state === 'initial_conversation' && (
                      <Button 
                        size="sm" 
                        className="btn-primary flex-grow sm:flex-grow-0"
                        onClick={() => setShowPitchModal(true)}
                        data-testid="next-action-propose-pitch-btn"
                      >
                        <Video size={14} className="mr-2" />
                        Propose Pitch Session
                      </Button>
                    )}
                    {milestone?.current_state === 'pitch_proposed' && milestone?.proposed_by !== user?.user_id && (
                      <Button 
                        size="sm" 
                        className="btn-primary flex-grow sm:flex-grow-0"
                        onClick={() => setShowConfirmModal(true)}
                        data-testid="next-action-accept-pitch-btn"
                      >
                        <CheckCircle2 size={14} className="mr-2" />
                        Accept & Confirm Slot
                      </Button>
                    )}
                    {milestone?.current_state === 'pitch_proposed' && milestone?.proposed_by === user?.user_id && (
                      <span className={`text-sm ${textMuted} italic`}>Waiting for response...</span>
                    )}
                    {milestone?.current_state === 'pitch_session_scheduled' && (
                      <Button 
                        size="sm" 
                        className="btn-primary flex-grow sm:flex-grow-0"
                        onClick={() => handleMilestoneAction('mark_pitch_completed')}
                        data-testid="next-action-mark-completed-btn"
                      >
                        <CheckCircle2 size={14} className="mr-2" />
                        Mark Pitch Completed
                      </Button>
                    )}
                    {milestone?.current_state === 'pitch_session_completed' && (
                      <Button 
                        size="sm" 
                        className="btn-primary flex-grow sm:flex-grow-0"
                        onClick={() => handleMilestoneAction('move_to_due_diligence')}
                        data-testid="next-action-move-dd-btn"
                      >
                        Move to Due Diligence
                      </Button>
                    )}
                    {milestone?.current_state === 'due_diligence' && (
                      <Button 
                        size="sm" 
                        className="btn-primary flex-grow sm:flex-grow-0"
                        onClick={() => setShowDecisionModal(true)}
                        data-testid="next-action-record-decision-btn"
                      >
                        Record Investment Decision
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Match Status Indicator */}
              <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20 text-green-400">
                    <CheckCircle2 size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-green-400 font-medium">You are Matched!</p>
                    <p className="text-xs text-green-400/70">
                      {milestone?.current_state === 'matched' && 'Start building your relationship'}
                      {milestone?.current_state === 'initial_conversation' && 'Initial conversation held'}
                      {milestone?.current_state === 'pitch_proposed' && (milestone?.proposed_by === user?.user_id ? 'Awaiting response to your pitch proposal' : 'Pitch proposal pending your response')}
                      {milestone?.current_state === 'pitch_session_scheduled' && `Pitch scheduled for ${new Date(milestone?.confirmed_pitch_slot).toLocaleDateString()}`}
                      {milestone?.current_state === 'pitch_session_completed' && 'Pitch session completed'}
                      {milestone?.current_state === 'due_diligence' && 'In due diligence phase'}
                      {milestone?.current_state === 'investment_decision' && `Decision: ${milestone?.investment_decision}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Relationship Actions Section */}
              <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}>
                <h3 className={`font-semibold mb-3 ${textPrimary}`}>Relationship Actions</h3>
                
                {/* Pending Proposal Alert - for recipient */}
                {milestone?.current_state === 'pitch_proposed' && milestone?.proposed_by !== user?.user_id && (
                  <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-amber-500 font-medium text-sm mb-2">Pitch Proposal Pending</p>
                    <p className={`text-xs ${textMuted} mb-3`}>
                      {profile?.name} has proposed a pitch session
                      {milestone?.proposal_message && `: "${milestone.proposal_message}"`}
                    </p>
                    {milestone?.meeting_platform && (
                      <p className={`text-xs ${textMuted} mb-2`}>
                        Meeting format: {milestone.meeting_platform === 'zoom' ? 'Zoom' : milestone.meeting_platform === 'phone' ? 'Phone' : 'In-person'}
                        {milestone?.meeting_link && <span className="ml-2">• <a href={milestone.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">Meeting link</a></span>}
                      </p>
                    )}
                    <p className={`text-xs ${textMuted} mb-3`}>Proposed times:</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {milestone?.proposed_pitch_slots?.map((slot, idx) => (
                        <span key={idx} className={`px-3 py-1.5 rounded-md text-xs ${isDark ? 'bg-slate-700' : 'bg-white border border-slate-200'} ${textPrimary}`}>
                          {new Date(slot).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" className="btn-primary" onClick={() => setShowConfirmModal(true)} data-testid="accept-confirm-slot-btn">
                        <CheckCircle2 size={14} className="mr-1" />
                        Accept & Confirm Slot
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowNewTimeModal(true)} data-testid="propose-new-time-btn">
                        <Clock size={14} className="mr-1" />
                        Propose New Time
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleDeclinePitch} className="text-red-500 hover:text-red-600" data-testid="decline-pitch-btn">
                        <XCircle size={14} className="mr-1" />
                        Decline
                      </Button>
                    </div>
                  </div>
                )}

                {/* Awaiting Response - for proposer */}
                {milestone?.current_state === 'pitch_proposed' && milestone?.proposed_by === user?.user_id && (
                  <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                    <p className="text-blue-500 font-medium text-sm mb-1">Awaiting Response</p>
                    <p className={`text-xs ${textMuted}`}>
                      Your pitch proposal has been sent. Waiting for {profile?.name} to respond.
                    </p>
                  </div>
                )}

                {/* Scheduled Session Info */}
                {milestone?.current_state === 'pitch_session_scheduled' && milestone?.confirmed_pitch_slot && (
                  <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <p className="text-green-500 font-medium text-sm mb-1">Pitch Session Scheduled</p>
                    <p className={`text-sm ${textPrimary}`}>
                      {new Date(milestone.confirmed_pitch_slot).toLocaleString('en-US', { 
                        weekday: 'long', month: 'long', day: 'numeric', 
                        hour: '2-digit', minute: '2-digit' 
                      })}
                    </p>
                    {milestone?.meeting_platform && (
                      <p className={`text-xs ${textMuted} mt-1`}>
                        Via {milestone.meeting_platform === 'zoom' ? 'Zoom' : milestone.meeting_platform === 'phone' ? 'Phone' : 'In-person'}
                        {milestone?.meeting_link && <span> • <a href={milestone.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">Join meeting</a></span>}
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="btn-primary" onClick={() => handleMilestoneAction('mark_pitch_completed')} data-testid="mark-completed-btn">
                        Mark Completed
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleMilestoneAction('mark_pitch_rescheduled')} data-testid="reschedule-btn">
                        Reschedule
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleMilestoneAction('mark_pitch_noshow')} data-testid="no-show-btn">
                        No-show
                      </Button>
                    </div>
                  </div>
                )}

                {/* Action Buttons - Show when no pending proposal */}
                {(!milestone?.current_state || ['matched', 'initial_conversation'].includes(milestone?.current_state)) && (
                  <div className="flex flex-wrap gap-2">
                    {milestone?.current_state === 'matched' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleMilestoneAction('mark_initial_conversation')}
                        className={isDark ? 'border-slate-600' : ''}
                        data-testid="request-initial-call-btn"
                      >
                        <Phone size={14} className="mr-1" />
                        Request Initial Call
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      className="btn-primary"
                      onClick={() => setShowPitchModal(true)}
                      data-testid="propose-pitch-session-btn"
                    >
                      <Video size={14} className="mr-1" />
                      Propose Pitch Session
                    </Button>
                    <Link to="/relationships">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={isDark ? 'border-slate-600' : ''}
                        data-testid="view-relationship-progress-btn"
                      >
                        <GitBranch size={14} className="mr-1" />
                        View Relationship Progress
                      </Button>
                    </Link>
                  </div>
                )}

                {/* Post-Pitch Actions */}
                {milestone?.current_state === 'pitch_session_completed' && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="btn-primary" onClick={() => handleMilestoneAction('move_to_due_diligence')} data-testid="move-to-dd-btn">
                      Move to Due Diligence
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {/* Open decision modal */}} data-testid="record-decision-btn">
                      Record Investment Decision
                    </Button>
                    <Link to="/relationships">
                      <Button size="sm" variant="outline" className={isDark ? 'border-slate-600' : ''}>
                        <GitBranch size={14} className="mr-1" />
                        View Progress
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Message / Chat Action */}
          <div className={`p-4 rounded-lg border ${
            isMatched && featureAccess.messaging.allowed 
              ? actionBg 
              : actionBgDisabled
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isMatched && featureAccess.messaging.allowed 
                    ? iconBg 
                    : iconBgDisabled
                }`}>
                  <MessageSquare size={20} />
                </div>
                <div>
                  <p className={`font-medium ${textPrimary}`}>Message / Chat</p>
                  <p className={`text-xs ${textSecondary}`}>
                    {!isMatched 
                      ? '⏳ Available after matching'
                      : featureAccess.messaging.allowed 
                        ? '✓ Available in your plan' 
                        : `🔒 Requires ${featureAccess.messaging.requiredPlan}`
                    }
                  </p>
                </div>
              </div>
              <div className="relative group">
                <Button
                  onClick={handleMessage}
                  disabled={!isMatched}
                  className={`${
                    isMatched && featureAccess.messaging.allowed
                      ? 'btn-primary' 
                      : isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-500 hover:bg-slate-600'
                  }`}
                >
                  {!isMatched ? 'Match First' : 'Open Chat'}
                </Button>
                {/* Tooltip for Match First explanation */}
                {!isMatched && (
                  <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    <p className="font-semibold mb-1">How to match:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Click "Like" above to express interest</li>
                      <li>If they also like you back, you're matched!</li>
                      <li>Then messaging becomes available</li>
                    </ol>
                    <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Save Profile Action (always available) */}
          <div className="p-4 rounded-lg border border-slate-300 bg-slate-100/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 ${textSecondary}">
                  <Bookmark size={20} />
                </div>
                <div>
                  <p className="${textPrimary} font-medium">Save Profile</p>
                  <p className="text-xs ${textSecondary}">✓ Available in your plan</p>
                </div>
              </div>
              <Button 
                onClick={() => toast.success('Profile saved to your list!')}
                variant="outline"
                className="border-slate-600 ${textSecondary} hover:bg-slate-700"
              >
                Save
              </Button>
            </div>
          </div>
        </div>

        {/* Plan upgrade hint */}
        {(!featureAccess.messaging.allowed || !featureAccess.unlimited_matches.allowed) && (
          <div className="mt-4 p-3 rounded-lg bg-blue-600/10 border border-blue-600/30">
            <p className="text-sm text-blue-500">
              Upgrade your plan to unlock all actions and connect directly with this {profileType}.
            </p>
          </div>
        )}
      </Card>
    );
  };

  const handleAcceptNda = async () => {
    try {
      await axios.post(`${API}/nda/accept`, { founder_id: userId });
      setNdaAccepted(true);
      setShowNdaModal(false);
      toast.success('NDA accepted. You can now access sensitive information.');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to accept NDA');
    }
  };

  const handleDownloadPitchDeck = async () => {
    try {
      const response = await axios.get(`${API}/profile/founder/pitch-deck/${userId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pitch-deck.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Pitch deck downloaded');
    } catch (error) {
      if (error.response?.status === 403) {
        setShowNdaModal(true);
      } else {
        toast.error('Failed to download pitch deck');
      }
    }
  };

  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const badgeBg = isDark ? 'bg-blue-900/60 text-blue-200 border border-blue-700/50' : 'bg-blue-100 text-blue-800';
  const badgeBgAlt = isDark ? 'bg-slate-600 text-slate-100 border border-slate-500' : 'bg-slate-200 text-slate-700';
  const innerBg = isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200';
  const btnBack = isDark ? 'text-slate-300 hover:text-white' : 'text-blue-800 hover:text-blue-900';

  if (loading) {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className={`text-center ${textSecondary}`}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className={`mb-6 ${btnBack}`}
          >
            <ArrowLeft size={18} className="mr-2" />
            Back
          </Button>
          <Card className={`shadow-sm p-12 text-center ${cardBg}`}>
            <p className={textSecondary}>{error || 'Profile not found'}</p>
          </Card>
        </div>
        {/* Quota Upgrade Modal - also render in no-profile state for quota exceeded */}
        <QuotaUpgradeModal
          isOpen={modalState.isOpen}
          onClose={() => {
            closeModal();
            navigate(-1);
          }}
          quotaType={modalState.quotaType}
          used={modalState.used}
          limit={modalState.limit}
          planName={modalState.planName}
          role={modalState.role || user?.role}
        />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg}`} data-testid="profile-view">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className={`mb-6 ${btnBack}`}
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back
        </Button>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <Card className={`mb-6 p-4 border-2 border-amber-500/50 ${isDark ? 'bg-amber-900/20' : 'bg-amber-50'}`} data-testid="preview-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500/20">
                <Eye size={20} className="text-amber-500" />
              </div>
              <div className="flex-1">
                <p className={`font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                  Preview Mode: Viewing as {previewMode === 'investor' ? 'an Investor' : 'a Founder'}
                </p>
                <p className={`text-sm ${isDark ? 'text-amber-300/70' : 'text-amber-700'}`}>
                  This is how {previewMode === 'investor' ? 'investors' : 'founders'} see your profile. Actions are hidden in preview mode.
                </p>
              </div>
              <Link to="/">
                <Button variant="outline" className={`${isDark ? 'border-amber-600 text-amber-300' : 'border-amber-600 text-amber-700'}`}>
                  Exit Preview
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Actions Panel - shown before profile content (hidden in preview mode) */}
        {!isPreviewMode && renderActionsPanel()}

        {/* Founder Profile */}
        {profileType === 'founder' && (
          <div className="space-y-6">
            <Card className={`shadow-sm p-8 ${cardBg}`}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className={`text-4xl font-bold mb-2 ${textPrimary}`}>{profile.startup_name}</h1>
                  <p className={`text-lg ${textSecondary}`}>Founded by {profile.name}</p>
                  <div className={`flex items-center gap-2 mt-2 ${textSecondary}`}>
                    <MapPin size={16} />
                    <span>{profile.city}, {profile.country}</span>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={badgeBg}>{profile.stage}</Badge>
                  {ndaAccepted && user?.role === 'investor' && (
                    <div className="nda-badge mt-2">NDA Signed</div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>One-line Pitch</h3>
                  <p className={`text-lg ${textPrimary}`}>{profile.one_line_pitch}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Industries</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.industries.map(ind => (
                        <span key={ind} className={`text-sm px-3 py-1 rounded ${badgeBg}`}>
                          {ind}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.tags.map(tag => (
                        <span key={tag} className={`text-sm px-3 py-1 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 ${textSecondary}'}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <Building2 size={20} className="text-blue-500" />
                  The Problem
                </h2>
                <p className={textSecondary}>{profile.problem}</p>
              </Card>

              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <TrendingUp size={20} className="text-blue-500" />
                  The Solution
                </h2>
                <p className="${textSecondary}">{profile.solution}</p>
              </Card>
            </div>

            <Card className="${cardBg} shadow-sm p-6">
              <h2 className="text-xl font-bold ${textPrimary} mb-4">Target Customer</h2>
              <p className="${textSecondary}">{profile.target_customer}</p>
            </Card>

            {profile.traction && (
              <Card className="${cardBg} shadow-sm p-6">
                <h2 className="text-xl font-bold ${textPrimary} mb-4">Traction</h2>
                <p className="${textSecondary}">{profile.traction}</p>
              </Card>
            )}

            <Card className="${cardBg} shadow-sm p-6">
              <h2 className="text-xl font-bold ${textPrimary} mb-4">Funding</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="${textSecondary} text-sm mb-2">Amount Seeking</h3>
                  <p className="text-2xl font-bold text-blue-600">
                    {profile.currency} {profile.funding_asked_min.toLocaleString()} - {profile.funding_asked_max.toLocaleString()}
                  </p>
                </div>
                <div>
                  <h3 className="${textSecondary} text-sm mb-2">Use of Funds</h3>
                  <p className="${textSecondary}">{profile.use_of_funds}</p>
                </div>
              </div>
            </Card>

            <Card className="${cardBg} shadow-sm p-6">
              <h2 className="text-xl font-bold ${textPrimary} mb-4">Team</h2>
              <p className="${textSecondary}">{profile.team_summary}</p>
            </Card>

            {/* Investment Alignment Section - Always show, use "Not provided" for empty fields */}
            <Card className={`shadow-sm p-6 ${cardBg}`}>
              <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Investment Alignment</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Seeking Investors</h3>
                  {profile.primary_investor_type_sought?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.primary_investor_type_sought.map(type => (
                        <span key={type} className={`text-sm px-2 py-1 rounded ${badgeBg}`}>{type}</span>
                      ))}
                    </div>
                  ) : (
                    <FieldValue value={null} textClass={textPrimary} />
                  )}
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Previous Funding</h3>
                  <FieldValue value={profile.previous_funding} textClass={textPrimary} />
                  {profile.amount_raised_to_date && (
                    <p className={`text-sm ${textMuted}`}>
                      Raised: {profile.currency} {profile.amount_raised_to_date.toLocaleString()}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Round Structure</h3>
                  <FieldValue value={profile.target_round_structure} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Target Valuation</h3>
                  <FieldValue value={profile.target_valuation_range} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Founder Ownership</h3>
                  <FieldValue value={profile.founder_ownership} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Growth Ambition</h3>
                  <FieldValue value={profile.growth_ambition} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Exit Vision</h3>
                  <FieldValue value={profile.exit_vision} textClass={textPrimary} />
                </div>
              </div>
            </Card>

            {/* Capital Strategy Section - Always show */}
            <Card className={`shadow-sm p-6 ${cardBg}`}>
              <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Capital Strategy</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Last Round Type</h3>
                  <FieldValue value={profile.last_round_type} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Current Raise Type</h3>
                  <FieldValue value={profile.current_raise_type} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Target Investor Type</h3>
                  <FieldValue value={profile.target_investor_type} textClass={textPrimary} />
                </div>
              </div>
            </Card>

            {user?.role === 'investor' && (
              <>
                <Card className="${cardBg} shadow-sm p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-xl font-bold ${textPrimary} mb-2 flex items-center gap-2">
                        <Shield size={20} className="text-blue-500" />
                        Sensitive Details
                      </h2>
                      {ndaAccepted ? (
                        <p className="${textSecondary} whitespace-pre-wrap">{profile.sensitive_details}</p>
                      ) : (
                        <div>
                          <p className="${textSecondary} mb-4">This information is protected by an NDA.</p>
                          <Button onClick={() => setShowNdaModal(true)} className="btn-primary" data-testid="view-nda">
                            <Shield size={16} className="mr-2" />
                            Sign NDA to View
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>

                <Card className="${cardBg} shadow-sm p-6">
                  <h2 className="text-xl font-bold ${textPrimary} mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-blue-500" />
                    Pitch Deck
                  </h2>
                  {profile.pitch_deck_filename && ndaAccepted ? (
                    <Button onClick={handleDownloadPitchDeck} className="btn-primary" data-testid="download-pitch-deck">
                      <Download size={16} className="mr-2" />
                      Download Pitch Deck (PDF)
                    </Button>
                  ) : profile.pitch_deck_filename ? (
                    <div>
                      <p className="${textSecondary} mb-4">Pitch deck available after NDA acceptance.</p>
                      <Button onClick={() => setShowNdaModal(true)} className="btn-primary" data-testid="sign-nda-for-deck">
                        <Shield size={16} className="mr-2" />
                        Sign NDA to Access
                      </Button>
                    </div>
                  ) : (
                    <p className="${textSecondary}">No pitch deck uploaded yet.</p>
                  )}
                </Card>
              </>
            )}
          </div>
        )}

        {/* Investor Profile */}
        {profileType === 'investor' && (
          <div className="space-y-6">
            <Card className="${cardBg} shadow-sm p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-4xl font-bold ${textPrimary} mb-2">{profile.name}</h1>
                  <p className="${textSecondary} text-lg capitalize">{profile.investor_type}</p>
                  <div className="flex items-center gap-2 ${textSecondary} mt-2">
                    <MapPin size={16} />
                    <span>{profile.city}, {profile.country}</span>
                  </div>
                </div>
                {profile.verified && (
                  <Badge className="bg-green-500/20 text-green-400 px-3 py-1">
                    <Shield size={14} className="mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              <div>
                <h3 className="${textSecondary} text-sm font-medium mb-2">Bio</h3>
                <p className="${textSecondary}">{profile.bio}</p>
              </div>

              {profile.linkedin_url && (
                <a
                  href={profile.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-500 mt-4"
                >
                  <ExternalLink size={16} />
                  View LinkedIn Profile
                </a>
              )}
            </Card>

            <Card className="${cardBg} shadow-sm p-6">
              <h2 className="text-xl font-bold ${textPrimary} mb-4">Investment Criteria</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="${textSecondary} text-sm mb-2">Ticket Size</h3>
                  <p className="text-2xl font-bold text-blue-600">
                    {profile.currency} {profile.ticket_size_min.toLocaleString()} - {profile.ticket_size_max.toLocaleString()}
                  </p>
                </div>
                <div>
                  <h3 className="${textSecondary} text-sm mb-2">Preferred Stages</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.preferred_stages.map(stage => (
                      <span key={stage} className="text-sm bg-blue-100 ${textSecondary} px-3 py-1 rounded">
                        {stage}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="${textSecondary} text-sm mb-2">Preferred Industries</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.preferred_industries.map(ind => (
                      <span key={ind} className="text-sm bg-slate-100 ${textSecondary} px-3 py-1 rounded">
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="${textSecondary} text-sm mb-2">Geography Focus</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.geography_focus.map(geo => (
                      <span key={geo} className="text-sm bg-slate-100 ${textSecondary} px-3 py-1 rounded">
                        {geo}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Investment Preferences Section - Always show with "Not provided" placeholders */}
            <Card className={`shadow-sm p-6 ${cardBg}`}>
              <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Investment Preferences</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Preferred Round Structure</h3>
                  {profile.preferred_round_structure?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.preferred_round_structure.map(type => (
                        <span key={type} className={`text-sm px-2 py-1 rounded ${badgeBg}`}>{type}</span>
                      ))}
                    </div>
                  ) : (
                    <FieldValue value={null} textClass={textPrimary} />
                  )}
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Check Size Preference</h3>
                  <FieldValue value={profile.check_size_preference} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Ownership Preference</h3>
                  <FieldValue value={profile.ownership_preference} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Growth Preference</h3>
                  <FieldValue value={profile.growth_preference} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Exit Horizon</h3>
                  <FieldValue value={profile.exit_horizon} textClass={textPrimary} />
                </div>
              </div>
            </Card>

            {/* Investment Strategy Section - Always show */}
            <Card className={`shadow-sm p-6 ${cardBg}`}>
              <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Investment Strategy</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Preferred Sectors</h3>
                  {profile.preferred_sectors?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.preferred_sectors.map(sector => (
                        <span key={sector} className={`text-sm px-2 py-1 rounded ${badgeBg}`}>{sector}</span>
                      ))}
                    </div>
                  ) : (
                    <FieldValue value={null} textClass={textPrimary} />
                  )}
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Co-Investment Openness</h3>
                  <FieldValue value={profile.co_investment_openness} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Investment Horizon</h3>
                  <FieldValue value={profile.investment_horizon} textClass={textPrimary} />
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${textSecondary}`}>Value-Add Type</h3>
                  <FieldValue value={profile.value_add_type} textClass={textPrimary} />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Cofounder Profile */}
        {profileType === 'cofounder' && (
          <div className="space-y-6">
            <Card className="${cardBg} shadow-sm p-8">
              <h1 className="text-4xl font-bold ${textPrimary} mb-2">{profile.name}</h1>
              <div className="flex items-center gap-2 ${textSecondary} mb-4">
                <MapPin size={16} />
                <span>{profile.city}, {profile.country}</span>
              </div>

              <p className="${textSecondary}">{profile.bio}</p>
              {profile.linkedin_url && (
                <a
                  href={profile.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-500 mt-4"
                >
                  <ExternalLink size={16} />
                  View LinkedIn Profile
                </a>
              )}
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="${cardBg} shadow-sm p-6">
                <h2 className="text-xl font-bold ${textPrimary} mb-4">My Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map(skill => (
                    <span key={skill} className="text-sm bg-blue-100 ${textSecondary} px-3 py-2 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </Card>

              <Card className="${cardBg} shadow-sm p-6">
                <h2 className="text-xl font-bold ${textPrimary} mb-4">Looking For</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.looking_for.map(skill => (
                    <span key={skill} className="text-sm bg-slate-100 ${textSecondary} px-3 py-2 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </Card>
            </div>

            <Card className="${cardBg} shadow-sm p-6">
              <h2 className="text-xl font-bold ${textPrimary} mb-4">Interested Industries</h2>
              <div className="flex flex-wrap gap-2">
                {profile.industries.map(ind => (
                  <span key={ind} className="text-sm bg-slate-100 ${textSecondary} px-3 py-2 rounded">
                    {ind}
                  </span>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={closeUpgradeModal}
        feature={upgradeModal.feature}
        currentPlan={upgradeModal.currentPlan}
        requiredPlan={upgradeModal.requiredPlan}
        requiredPriceMonthly={upgradeModal.requiredPriceMonthly}
        requiredPriceYearly={upgradeModal.requiredPriceYearly}
        availablePlans={upgradeModal.availablePlans}
        role={upgradeModal.role}
      />

      {/* NDA Modal */}
      <Dialog open={showNdaModal} onOpenChange={setShowNdaModal}>
        <DialogContent className="bg-[#F2F6FA] ${textPrimary} border-slate-200 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Shield className="text-blue-500" />
              Non-Disclosure Agreement
            </DialogTitle>
            <DialogDescription className="${textSecondary}">
              Please read and accept the NDA to access sensitive information
            </DialogDescription>
          </DialogHeader>
          <div className="bg-[#F2F6FA]/50 p-6 rounded-lg my-4 max-h-96 overflow-y-auto">
            <pre className="${textSecondary} text-sm whitespace-pre-wrap font-sans">{NDA_TEMPLATE}</pre>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNdaModal(false)} className="${textSecondary}">
              Cancel
            </Button>
            <Button onClick={handleAcceptNda} className="btn-primary" data-testid="accept-nda">
              I Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Quota Upgrade Modal */}
      <QuotaUpgradeModal
        isOpen={modalState.isOpen}
        onClose={() => {
          closeModal();
          navigate(-1); // Go back when quota exceeded
        }}
        quotaType={modalState.quotaType}
        used={modalState.used}
        limit={modalState.limit}
        planName={modalState.planName}
        role={modalState.role || user?.role}
      />

      {/* Propose Pitch Session Modal */}
      <Dialog open={showPitchModal} onOpenChange={setShowPitchModal}>
        <DialogContent className={isDark ? 'bg-slate-800 text-white border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle>Propose Pitch Session</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              Select up to 3 date/time options and provide meeting details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className={`text-sm font-medium ${textSecondary}`}>Proposed Times (select at least 1)</label>
              {proposedSlots.map((slot, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className={`text-sm ${textMuted} w-16`}>Option {idx + 1}:</span>
                  <Input
                    type="datetime-local"
                    value={slot}
                    onChange={(e) => {
                      const newSlots = [...proposedSlots];
                      newSlots[idx] = e.target.value;
                      setProposedSlots(newSlots);
                    }}
                    className={isDark ? 'bg-slate-700 border-slate-600' : ''}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <label className={`text-sm font-medium ${textSecondary}`}>Meeting Format</label>
              <Select value={meetingPlatform} onValueChange={setMeetingPlatform}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="in_person">In-person</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className={`text-sm font-medium ${textSecondary}`}>Meeting Link (optional)</label>
              <Input
                type="url"
                placeholder="https://zoom.us/j/..."
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                className={isDark ? 'bg-slate-700 border-slate-600' : ''}
              />
            </div>

            <div className="space-y-2">
              <label className={`text-sm font-medium ${textSecondary}`}>Message (optional)</label>
              <Textarea
                placeholder="Looking forward to discussing..."
                value={proposalMessage}
                onChange={(e) => setProposalMessage(e.target.value)}
                className={isDark ? 'bg-slate-700 border-slate-600' : ''}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPitchModal(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleProposePitch} disabled={actionLoading} className="btn-primary">
              {actionLoading ? 'Sending...' : 'Send Proposal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Pitch Session Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className={isDark ? 'bg-slate-800 text-white border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle>Confirm Pitch Session</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              Select one of the proposed time slots to confirm the meeting.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {milestone?.proposed_pitch_slots?.map((slot, idx) => (
              <label key={idx} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedSlot === slot 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : isDark ? 'border-slate-600 hover:border-slate-500' : 'border-slate-200 hover:border-slate-300'
              }`}>
                <input
                  type="radio"
                  name="selectedSlot"
                  value={slot}
                  checked={selectedSlot === slot}
                  onChange={(e) => setSelectedSlot(e.target.value)}
                  className="text-blue-500"
                />
                <span className={textPrimary}>
                  {new Date(slot).toLocaleString('en-US', { 
                    weekday: 'short', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              </label>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmPitch} disabled={actionLoading || !selectedSlot} className="btn-primary">
              {actionLoading ? 'Confirming...' : 'Confirm Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Propose New Time Modal */}
      <Dialog open={showNewTimeModal} onOpenChange={setShowNewTimeModal}>
        <DialogContent className={isDark ? 'bg-slate-800 text-white border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle>Propose Alternative Times</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              The proposed times don't work? Suggest alternative times.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className={`text-sm font-medium ${textSecondary}`}>Your Proposed Times</label>
              {proposedSlots.map((slot, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className={`text-sm ${textMuted} w-16`}>Option {idx + 1}:</span>
                  <Input
                    type="datetime-local"
                    value={slot}
                    onChange={(e) => {
                      const newSlots = [...proposedSlots];
                      newSlots[idx] = e.target.value;
                      setProposedSlots(newSlots);
                    }}
                    className={isDark ? 'bg-slate-700 border-slate-600' : ''}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <label className={`text-sm font-medium ${textSecondary}`}>Meeting Format</label>
              <Select value={meetingPlatform} onValueChange={setMeetingPlatform}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="in_person">In-person</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className={`text-sm font-medium ${textSecondary}`}>Meeting Link (optional)</label>
              <Input
                type="url"
                placeholder="https://zoom.us/j/..."
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                className={isDark ? 'bg-slate-700 border-slate-600' : ''}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTimeModal(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleProposeNewTime} disabled={actionLoading} className="btn-primary">
              {actionLoading ? 'Sending...' : 'Send New Times'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Investment Decision Modal */}
      <Dialog open={showDecisionModal} onOpenChange={setShowDecisionModal}>
        <DialogContent className={isDark ? 'bg-slate-800 text-white border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle>Record Investment Decision</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              Record the outcome of your due diligence process for {profile?.startup_name || profile?.name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : ''}`}>Decision</label>
              <Select value={investmentDecision} onValueChange={setInvestmentDecision}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                  <SelectValue placeholder="Select decision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invested">Investing</SelectItem>
                  <SelectItem value="passed">Passing</SelectItem>
                  <SelectItem value="pending">Still Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionModal(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleRecordDecision} disabled={actionLoading} className="btn-primary">
              {actionLoading ? 'Recording...' : 'Record Decision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
